import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type { StoreOrOutletSearchResult } from '~/types'

export const useStoresOrOutletsStore = defineStore('storesoroutlets', () => {
  const searchTerm = ref<string>()
  const searchStore = useSearchStore()
  const user = useUserStore()
  const queryClient = useQueryClient()
  const fallbackLocation = user.locale.latitude !== Infinity && user.locale.longitude !== Infinity

  const query = computed(() => ({
    searchTerm: searchTerm.value,
    longSw: searchStore.query.longSw,
    latSw: searchStore.query.latSw,
    longNe: searchStore.query.longNe,
    latNe: searchStore.query.latNe,
    longitude: fallbackLocation ? user.locale.longitude : undefined,
    latitude: fallbackLocation ? user.locale.latitude : undefined
  }))

  const queryResult = useQuery({
    queryKey: () => ['storeoroutlets', 'list', query.value],
    queryFn: () =>
      $fetch<Array<StoreOrOutletSearchResult>>('/api/storeoroutlets', {
        query: query.value
      }),
    select: data => {
      return data.sort((a, b) => {
        if (a.country === b.country) return 0
        if (a.country === user.countryName) return -1
        if (b.country === user.countryName) return 1
        return 0
      })
    },
    enabled: () => searchTerm.value !== undefined,
    staleTime: 30000 // 30 seconds
  })

  // actions
  function search(term: string) {
    searchTerm.value = term

    if (queryResult.status.value === 'idle' || queryResult.status.value === 'success') {
      queryResult.refetch()
    }
  }

  function reset() {
    searchTerm.value = undefined
    queryClient.removeQueries({ queryKey: ['storeoroutlets', 'list'] })
  }

  return {
    // state
    pending: queryResult.isFetching,
    results: computed(() => queryResult.data.value ?? []),
    searchTerm: computed(() => searchTerm.value),
    status: queryResult.status,

    // getters
    query,

    // actions
    search,
    reset
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStoresOrOutletsStore, import.meta.hot))
}
